import React, { useEffect, useState } from "react";
import { Download, UserPlus, X, Mail, Users, MessageSquare, Star, UserCheck, Calculator, CalendarCheck, Home } from "lucide-react";
import { subscribersApi, contactsApi } from "../../api";
import { toast } from "sonner";

const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString(); } catch { return ""; } };

const TABS = [
  ["members", "Members", UserCheck],
  ["subscribers", "Subscribers", Mail],
  ["numbers", "Number Requests", Calculator],
  ["inquiries", "Deal Inquiries", MessageSquare],
  ["submissions", "Submitted Deals", Home],
  ["appointments", "Appointments", CalendarCheck],
  ["partners", "Partner Apps", Star],
  ["leads", "Leads", Users],
];

function toCSV(rows) {
  if (!rows.length) return "";
  const keys = Object.keys(rows[0]);
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  return [keys.join(","), ...rows.map((r) => keys.map((k) => esc(r[k])).join(","))].join("\n");
}

function downloadCSV(name, rows) {
  const csv = toCSV(rows);
  if (!csv) { toast.error("Nothing to export"); return; }
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = `${name}.csv`; a.click();
  URL.revokeObjectURL(url);
}

function AddContactModal({ onClose, onSaved }) {
  const [f, setF] = useState({ firstName: "", lastName: "", email: "", states: "", frequency: "Daily" });
  const save = async () => {
    if (!f.email || !f.email.includes("@")) { toast.error("A valid email is required"); return; }
    try {
      await contactsApi.addSubscriber(f);
      toast.success("Contact added to subscribers");
      onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add contact"); }
  };
  return (
    <div onClick={onClose} data-testid="add-contact-overlay" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", backdropFilter: "blur(6px)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: "1.5rem" }}>
      <div onClick={(e) => e.stopPropagation()} className="card" style={{ background: "#111015", width: "100%", maxWidth: 440, padding: "2.2rem", position: "relative", borderColor: "rgba(201,162,75,0.2)" }}>
        <button onClick={onClose} style={{ position: "absolute", top: 18, right: 18, background: "none", border: "none", color: "var(--muted)", cursor: "pointer" }}><X size={20} /></button>
        <h3 className="font-serif" style={{ fontSize: "1.5rem", marginBottom: "1.2rem" }}>Add Contact</h3>
        <div style={{ display: "grid", gap: "0.8rem" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.7rem" }}>
            <input className="field" data-testid="contact-first-name" placeholder="First name" value={f.firstName} onChange={(e) => setF({ ...f, firstName: e.target.value })} />
            <input className="field" data-testid="contact-last-name" placeholder="Last name" value={f.lastName} onChange={(e) => setF({ ...f, lastName: e.target.value })} />
          </div>
          <input className="field" data-testid="contact-email" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
          <input className="field" data-testid="contact-states" placeholder="Interested states (e.g. FL, TX)" value={f.states} onChange={(e) => setF({ ...f, states: e.target.value })} />
          <select className="field" value={f.frequency} onChange={(e) => setF({ ...f, frequency: e.target.value })}>
            {["Daily", "Weekly", "Instant"].map((x) => <option key={x}>{x}</option>)}
          </select>
          <button className="btn-gold" data-testid="save-contact-btn" style={{ width: "100%" }} onClick={save}>Add Contact</button>
        </div>
      </div>
    </div>
  );
}

const Th = ({ children }) => <th style={{ fontSize: "0.62rem", letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--muted-2)", fontWeight: 400, padding: "0 0.8rem 1rem" }}>{children}</th>;
const Td = ({ children, color }) => <td style={{ padding: "0.9rem 0.8rem", fontSize: "0.84rem", color: color || "var(--text)" }}>{children}</td>;

export default function ContactsView() {
  const [tab, setTab] = useState("members");
  const [data, setData] = useState({ members: [], subscribers: [], numbers: [], inquiries: [], submissions: [], appointments: [], partners: [], leads: [] });
  const [showAdd, setShowAdd] = useState(false);

  const load = async () => {
    try {
      const [m, s, n, i, sub, ap, p, l] = await Promise.all([
        contactsApi.members(), subscribersApi.list(), contactsApi.numberRequests(),
        contactsApi.inquiries(), contactsApi.dealSubmissions(), contactsApi.appointments(), contactsApi.partners(), contactsApi.leads(),
      ]);
      setData({ members: m, subscribers: s, numbers: n, inquiries: i, submissions: sub, appointments: ap, partners: p, leads: l });
    } catch (e) { toast.error("Failed to load contacts"); }
  };
  useEffect(() => { load(); }, []);

  const rows = data[tab] || [];

  const removeSub = async (id) => { await subscribersApi.remove(id); toast("Removed"); load(); };

  return (
    <div data-testid="contacts-view">
      <div style={{ display: "flex", gap: "0.6rem", flexWrap: "wrap", marginBottom: "1.4rem" }}>
        {TABS.map(([key, label, Icon]) => (
          <button key={key} data-testid={`contacts-tab-${key}`} onClick={() => setTab(key)}
            style={{ display: "flex", alignItems: "center", gap: 7, padding: "0.55rem 1rem", borderRadius: 999, cursor: "pointer", fontSize: "0.8rem", border: `1px solid ${tab === key ? "var(--gold)" : "rgba(255,255,255,0.12)"}`, background: tab === key ? "var(--gold-soft)" : "transparent", color: tab === key ? "var(--gold)" : "var(--muted)" }}>
            <Icon size={14} /> {label} <span style={{ opacity: 0.7 }}>({(data[key] || []).length})</span>
          </button>
        ))}
      </div>

      <div className="card" style={{ padding: "1.6rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.2rem", flexWrap: "wrap", gap: "0.8rem" }}>
          <span className="eyebrow" style={{ color: "var(--muted-2)" }}>{TABS.find((t) => t[0] === tab)[1]}</span>
          <div style={{ display: "flex", gap: "0.6rem" }}>
            {tab === "subscribers" && <button className="btn-gold" data-testid="open-add-contact" onClick={() => setShowAdd(true)}><UserPlus size={14} /> Add Contact</button>}
            <button className="btn-dark" data-testid="export-csv-btn" onClick={() => downloadCSV(tab, rows)}><Download size={13} /> Export CSV</button>
          </div>
        </div>

        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 720 }}>
            {tab === "members" && (
              <>
                <thead><tr style={{ textAlign: "left" }}><Th>Name</Th><Th>Email</Th><Th>Role</Th><Th>Signup</Th><Th>Joined</Th></tr></thead>
                <tbody>{rows.map((r) => (<tr key={r.id} style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}><Td>{r.firstName} {r.lastName}</Td><Td color="var(--muted)">{r.email}</Td><Td>{r.role || "—"}</Td><Td color="var(--gold)">{r.auth_provider === "google" ? "Google" : "Email"}</Td><Td color="var(--muted-2)">{fmtDate(r.created_at)}</Td></tr>))}</tbody>
              </>
            )}
            {tab === "numbers" && (
              <>
                <thead><tr style={{ textAlign: "left" }}><Th>Name</Th><Th>Email</Th><Th>Mode</Th><Th>DSCR</Th><Th>Cash Flow</Th><Th>Date</Th></tr></thead>
                <tbody>{rows.map((r) => (<tr key={r.id} style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}><Td>{r.name || "—"}</Td><Td color="var(--muted)">{r.email}</Td><Td>{r.mode || "—"}</Td><Td color="var(--gold)">{r.dscr ?? "—"}</Td><Td>{r.cashflow != null ? (r.cashflow >= 0 ? "+$" : "-$") + Math.abs(Math.round(r.cashflow)).toLocaleString() : "—"}</Td><Td color="var(--muted-2)">{fmtDate(r.created_at)}</Td></tr>))}</tbody>
              </>
            )}
            {tab === "submissions" && (
              <>
                <thead><tr style={{ textAlign: "left" }}><Th>Submitted By</Th><Th>Email</Th><Th>Phone</Th><Th>Address</Th><Th>Asking Price</Th><Th>Notes</Th><Th>Date</Th></tr></thead>
                <tbody>{rows.map((r) => (<tr key={r.id} style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}><Td>{r.name || "—"}</Td><Td color="var(--muted)">{r.email}</Td><Td>{r.phone || "—"}</Td><Td color="var(--gold)">{r.address}</Td><Td>{r.price || "—"}</Td><Td color="var(--muted)">{r.notes || "—"}</Td><Td color="var(--muted-2)">{fmtDate(r.created_at)}</Td></tr>))}</tbody>
              </>
            )}
            {tab === "appointments" && (
              <>
                <thead><tr style={{ textAlign: "left" }}><Th>Name</Th><Th>Email</Th><Th>Type</Th><Th>When</Th><Th>Booked</Th></tr></thead>
                <tbody>{rows.map((r) => (<tr key={r.id} style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}><Td>{r.name || "—"}</Td><Td color="var(--muted)">{r.email}</Td><Td>{r.event_type || "—"}</Td><Td color="var(--gold)">{r.start_time ? new Date(r.start_time).toLocaleString() : "—"}</Td><Td color="var(--muted-2)">{fmtDate(r.created_at)}</Td></tr>))}</tbody>
              </>
            )}
            {tab === "subscribers" && (
              <>
                <thead><tr style={{ textAlign: "left" }}><Th>Name</Th><Th>Email</Th><Th>States</Th><Th>Frequency</Th><Th>Joined</Th><Th></Th></tr></thead>
                <tbody>
                  {rows.map((s) => (
                    <tr key={s.id} style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
                      <Td>{s.firstName} {s.lastName}</Td><Td color="var(--muted)">{s.email}</Td><Td color="var(--gold)">{s.states || "—"}</Td><Td>{s.frequency}</Td><Td color="var(--muted-2)">{fmtDate(s.created_at)}</Td>
                      <Td><button onClick={() => removeSub(s.id)} data-testid={`del-sub-${s.id}`} style={{ background: "none", border: "none", color: "var(--red)", cursor: "pointer" }}><X size={14} /></button></Td>
                    </tr>
                  ))}
                </tbody>
              </>
            )}
            {tab === "inquiries" && (
              <>
                <thead><tr style={{ textAlign: "left" }}><Th>Name</Th><Th>Email</Th><Th>Phone</Th><Th>Down Payment</Th><Th>Date</Th></tr></thead>
                <tbody>{rows.map((r) => (<tr key={r.id} style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}><Td>{r.name || "—"}</Td><Td color="var(--muted)">{r.email || r.contact || "—"}</Td><Td>{r.phone || "—"}</Td><Td color="var(--gold)">{r.downPayment || "—"}</Td><Td color="var(--muted-2)">{fmtDate(r.created_at)}</Td></tr>))}</tbody>
              </>
            )}
            {tab === "partners" && (
              <>
                <thead><tr style={{ textAlign: "left" }}><Th>Name</Th><Th>Email</Th><Th>Company</Th><Th>Type</Th><Th>Date</Th></tr></thead>
                <tbody>{rows.map((r) => (<tr key={r.id} style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}><Td>{r.name}</Td><Td color="var(--muted)">{r.email}</Td><Td>{r.company || "—"}</Td><Td color="var(--gold)">{r.partnerType || "—"}</Td><Td color="var(--muted-2)">{fmtDate(r.created_at)}</Td></tr>))}</tbody>
              </>
            )}
            {tab === "leads" && (
              <>
                <thead><tr style={{ textAlign: "left" }}><Th>Name</Th><Th>Email</Th><Th>Phone</Th><Th>Goal</Th><Th>Date</Th></tr></thead>
                <tbody>{rows.map((r) => (<tr key={r.id} style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}><Td>{r.firstName} {r.lastName}</Td><Td color="var(--muted)">{r.email}</Td><Td>{r.phone || "—"}</Td><Td>{r.goal || "—"}</Td><Td color="var(--muted-2)">{fmtDate(r.created_at)}</Td></tr>))}</tbody>
              </>
            )}
          </table>
          {rows.length === 0 && <div style={{ padding: "2rem", textAlign: "center", color: "var(--muted-2)" }}>No records yet.</div>}
        </div>
      </div>
      {showAdd && <AddContactModal onClose={() => setShowAdd(false)} onSaved={load} />}
    </div>
  );
}
